import { v4 as uuidv4 } from "uuid";
import { notify } from "../api/photoshop";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { base64ToUint8Array, createBase64FromRawPixels } from "../utils/jimp";
import { isCropToolActive } from "./shared/check-crop-tool";
import { fetchGatewayInferenceJob } from "./shared/fetch-inference-job";

const TOOL_NAME = "Object Fusion";
const MAX_SIDE = 3072;

async function getLayerBase64(
	doc: any,
	layerId: number,
	canvasWidth: number,
	canvasHeight: number,
): Promise<string> {
	const { imageData, sourceBounds } = await photoshop.imaging.getPixels({
		documentID: doc.id,
		layerID: layerId,
		applyAlpha: true,
		sourceBounds: {
			left: 0,
			top: 0,
			right: canvasWidth,
			bottom: canvasHeight,
		},
	});

	const pixelData = await imageData.getData({});
	const sourceWidth = sourceBounds.right - sourceBounds.left;
	const sourceHeight = sourceBounds.bottom - sourceBounds.top;

	// Pad to full canvas if sourceBounds was clipped to layer content
	const needsPadding =
		sourceBounds.left !== 0 ||
		sourceBounds.top !== 0 ||
		sourceBounds.right !== canvasWidth ||
		sourceBounds.bottom !== canvasHeight;

	const paddingOptions = needsPadding
		? {
				top: sourceBounds.top,
				bottom: canvasHeight - sourceBounds.bottom,
				left: sourceBounds.left,
				right: canvasWidth - sourceBounds.right,
				color: [255, 255, 255],
			}
		: undefined;

	let targetWidth = canvasWidth;
	let targetHeight = canvasHeight;
	if (Math.max(canvasWidth, canvasHeight) > MAX_SIDE) {
		const scale = MAX_SIDE / Math.max(canvasWidth, canvasHeight);
		targetWidth = Math.round(canvasWidth * scale);
		targetHeight = Math.round(canvasHeight * scale);
	}

	const base64 = await createBase64FromRawPixels(
		pixelData as Uint8Array,
		sourceWidth,
		sourceHeight,
		paddingOptions,
		{ width: targetWidth, height: targetHeight },
	);

	imageData.dispose();
	return base64;
}

const callObjectFusion = async (
	messageId: string,
	jwt: string,
	tool_document_id: string,
) => {
	let success = false;

	if (isCropToolActive()) {
		await notify("Cannot generate images while the Crop tool is active.");
		return success;
	}

	await photoshop.core.executeAsModal(
		async (executionContext) => {
			const doc = photoshop.app.activeDocument;
			if (!doc) {
				await notify("Open a PSD file first");
				return;
			}

			if (
				doc.mode !== "RGBColorMode" ||
				doc.bitsPerChannel !== "bitDepth8"
			) {
				await notify("Image mode must be RGB Color, 8bits/Channel");
				return;
			}

			const activeLayers = doc.activeLayers;
			if (activeLayers.length !== 2) {
				await notify(
					"Select exactly 2 layers (top: object, bottom: background)",
				);
				return;
			}

			await doc.suspendHistory(async () => {
				try {
					// activeLayers[0] = top layer (object), activeLayers[1] = bottom layer (background)
					const objLayer = activeLayers[0];
					const bgLayer = activeLayers[1];

					const imgObj = await getLayerBase64(
						doc,
						objLayer.id,
						doc.width,
						doc.height,
					);
					const imgBg = await getLayerBase64(
						doc,
						bgLayer.id,
						doc.width,
						doc.height,
					);

					const data = {
						inputs: {
							$obj_img: imgObj,
							$bg_img: imgBg,
						},
						tool_document_id,
						jwt,
					};

					const result = await fetchObjectFusion(
						data,
						executionContext,
						messageId,
					);

					if (!result) return;

					const { width, height, rawPixels } =
						base64ToUint8Array(result);
					const uint8Array = new Uint8Array(rawPixels);
					const resultImageData =
						await photoshop.imaging.createImageDataFromBuffer(
							uint8Array,
							{
								width,
								height,
								components: 4,
								colorSpace: "RGB",
							},
						);

					const newLayer = await doc.createPixelLayer({
						name: TOOL_NAME,
					});

					if (!newLayer) {
						resultImageData.dispose();
						throw new Error("Failed to create layer");
					}

					await photoshop.imaging.putPixels({
						documentID: doc.id,
						layerID: newLayer.id,
						imageData: resultImageData,
						targetBounds: {
							left: 0,
							top: 0,
							right: doc.width,
							bottom: doc.height,
						},
						replace: true,
					});

					newLayer.selected = true;
					await photoshop.app.batchPlay(
						[{ _obj: "newPlacedLayer" }],
						{ synchronousExecution: true, modalBehavior: "execute" },
					);

					if (width > 0 && height > 0) {
						const targetLayer = doc.activeLayers[0];
						await targetLayer.scale(
							(doc.width / width) * 100,
							(doc.height / height) * 100,
							photoshop.constants.AnchorPosition.TOPLEFT,
						);
					}

					resultImageData.dispose();
					success = true;
				} catch (err) {
					console.error(err);
					await notify(`${err}`);
				}
			}, TOOL_NAME);
		},
		{ commandName: TOOL_NAME },
	);

	return success;
};

export async function fetchObjectFusion(
	params: {
		inputs: {
			$obj_img: string;
			$bg_img: string;
		};
		tool_document_id: string;
		jwt: string;
	},
	executionContext: any,
	messageId: string,
): Promise<string | false> {
	return fetchGatewayInferenceJob(params, executionContext, messageId);
}

export function useListenObjectFusionAMessage() {
	useListenFromRenderer([
		{
			key: "objectFusionA",
			onMessage: async (message) => {
				const success = await callObjectFusion(
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
